import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Ban, Plus, Trash2, Pencil } from 'lucide-react';
import {
  supabase, PageHead, DataTable, Tabs, Pill, UserCell, ProofButton, Modal, useRows, useUsers, useAction,
  notify, audit, isMissingFn, usd, fmtDate, fmtDateTime, userName,
} from '../adminShared';

/* ─── Features editor: add, remove, switch on/off ─────────────────────────── */
const toKey = (name) => name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
const toLabel = (key) => { const s = key.replace(/_/g, ' '); return s.charAt(0).toUpperCase() + s.slice(1); };

let rowId = 0;
function toRows(value) {
  let obj = value;
  if (typeof obj === 'string') { try { obj = JSON.parse(obj || '{}'); } catch { obj = {}; } }
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) obj = {};
  return Object.entries(obj).map(([k, v]) => ({ id: ++rowId, name: toLabel(k), active: !!v, raw: typeof v === 'boolean' ? undefined : v }));
}
function toObject(rows) {
  const out = {};
  rows.forEach(r => {
    const key = toKey(r.name);
    if (key) out[key] = r.raw !== undefined ? (r.active ? r.raw : false) : r.active;
  });
  return out;
}

function Switch({ on, onChange, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)}
      style={{
        width: 38, height: 22, borderRadius: 11, border: 0, padding: 2, flex: 'none', cursor: 'pointer',
        background: on ? '#16a34a' : 'rgba(128,128,128,.35)', transition: 'background .15s',
        display: 'flex', alignItems: 'center', justifyContent: on ? 'flex-end' : 'flex-start',
      }}>
      <span style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', display: 'block' }} />
    </button>
  );
}

function FeaturesEditor({ value, onChange }) {
  const [rows, setRows] = useState(() => toRows(value));
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commit = (next) => { setRows(next); onChange(toObject(next)); };
  const update = (id, patch) => commit(rows.map(r => r.id === id ? { ...r, ...patch } : r));
  const remove = (id) => commit(rows.filter(r => r.id !== id));
  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (!rows.some(r => toKey(r.name) === toKey(name))) commit([...rows, { id: ++rowId, name, active: true, raw: undefined }]);
    setDraft('');
    inputRef.current?.focus();
  };

  const box = { padding: '7px 10px', border: '1px solid rgba(128,128,128,.3)', borderRadius: 6, background: 'transparent', color: 'inherit', font: 'inherit', minWidth: 0 };

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {rows.length === 0 && <div style={{ opacity: .65, fontSize: 13 }}>No features yet. Add the first one below.</div>}
      {rows.map(r => (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: r.active ? 1 : .6 }}>
          <Switch on={r.active} onChange={v => update(r.id, { active: v })} label={`${r.name} active`} />
          <input value={r.name} onChange={e => update(r.id, { name: e.target.value })} aria-label="Feature name" style={{ ...box, flex: 1 }} />
          <span style={{ fontSize: 12, width: 40, opacity: .7 }}>{r.active ? 'Active' : 'Off'}</span>
          <button type="button" className="ad-btn ad-btn-no ad-btn-sm" onClick={() => remove(r.id)} aria-label={`Remove ${r.name}`}><Trash2 /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <input ref={inputRef} value={draft} placeholder="New feature, e.g. Priority support" onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} style={{ ...box, flex: 1 }} />
        <button type="button" className="ad-btn ad-btn-sm" onClick={add} disabled={!draft.trim()}><Plus />Add feature</button>
      </div>
    </div>
  );
}

/* ─── Add / edit plan ─────────────────────────────────────────────────────── */
const NUM_FIELDS = [
  { key: 'monthly_price',     label: 'Monthly price ($)',        required: true },
  { key: 'annual_price',      label: 'Annual price ($ / month)', required: true, help: 'Price per month when billed yearly' },
  { key: 'max_copy_traders',  label: 'Max copy traders',         required: true },
  { key: 'max_allocation',    label: 'Max allocation ($)',       help: 'Leave empty for no limit' },
  { key: 'max_alerts',        label: 'Max alerts',               required: true },
  { key: 'max_watchlist',     label: 'Max watchlist items',      required: true },
  { key: 'api_calls_per_day', label: 'API calls per day',        required: true },
];

function PlanModal({ plan, onClose, onSaved }) {
  const isNew = !plan.id;
  const [busy, run] = useAction();
  const [form, setForm] = useState(() => ({
    name: plan.name ?? '',
    ...Object.fromEntries(NUM_FIELDS.map(f => [f.key, plan[f.key] ?? ''])),
    features: plan.features ?? {},
  }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name.trim() && NUM_FIELDS.every(f => !f.required || form[f.key] !== '');

  const save = () => run('save', async () => {
    const payload = {
      name: form.name.trim(),
      features: form.features,
      ...Object.fromEntries(NUM_FIELDS.map(f => [f.key, form[f.key] === '' ? null : Number(form[f.key])])),
    };
    const q = supabase.from('subscription_plans');
    const { data, error } = isNew
      ? await q.insert(payload).select().maybeSingle()
      : await q.update(payload).eq('id', plan.id).select().maybeSingle();
    if (error) throw error;
    audit(isNew ? 'create' : 'update', 'subscription_plans', data?.id ?? plan.id, isNew ? null : plan, payload);
    onSaved();
  }, isNew ? 'Plan added' : 'Plan saved');

  const del = () => {
    if (!window.confirm(`Delete the ${plan.name} plan? Plans that users are subscribed to can’t be deleted.`)) return;
    run('del', async () => {
      const { error } = await supabase.from('subscription_plans').delete().eq('id', plan.id);
      if (error) throw error;
      audit('delete', 'subscription_plans', plan.id, plan, null);
      onSaved();
    }, 'Plan deleted');
  };

  return (
    <Modal title={isNew ? 'Add plan' : `Edit ${plan.name}`} onClose={onClose} wide
      footer={<>
        {!isNew && <button className="ad-btn ad-btn-no" onClick={del} disabled={!!busy} style={{ marginRight: 'auto' }}><Trash2 />Delete plan</button>}
        <button className="ad-btn ad-btn-ghost" onClick={onClose} disabled={!!busy}>Cancel</button>
        <button className="ad-btn ad-btn-accent" onClick={save} disabled={!valid || !!busy}>{isNew ? 'Add plan' : 'Save changes'}</button>
      </>}>
      <label className="ad-field"><span>Name</span>
        <input className="ad-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Basic, Pro, Elite" />
        <small>Must be unique</small></label>
      <div className="ad-form">
        {NUM_FIELDS.map(f => (
          <label key={f.key} className="ad-field"><span>{f.label}</span>
            <input className="ad-input" type="number" min="0" step="any" value={form[f.key]} onChange={e => set(f.key, e.target.value)} />
            {f.help && <small>{f.help}</small>}</label>
        ))}
      </div>
      <div className="ad-field"><span>Features</span>
        <FeaturesEditor value={form.features} onChange={v => set('features', v)} />
      </div>
    </Modal>
  );
}

/* ─── Add / change a user's subscription ──────────────────────────────────── */
function SubModal({ sub, plans, users, onClose, onSaved }) {
  const isNew = !sub.id;
  const [busy, run] = useAction();
  const [q, setQ] = useState('');
  const [userId, setUserId] = useState(sub.user_id || '');
  const [planId, setPlanId] = useState(sub.plan_id ?? plans[0]?.id ?? '');
  const [cycle, setCycle] = useState(sub.billing_cycle || 'monthly');
  const [end, setEnd] = useState(sub.current_period_end ? String(sub.current_period_end).slice(0, 10) : '');
  const list = Object.values(users || {}).filter(u => u && u.id && `${userName(u)} ${u.email}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 50);
  const plan = plans.find(p => String(p.id) === String(planId));
  const valid = !!userId && !!plan;

  const save = () => run('sub', async () => {
    const now = new Date();
    let endIso;
    if (end) endIso = new Date(`${end}T23:59:59`).toISOString();
    else { const d = new Date(now); if (cycle === 'annual') d.setFullYear(d.getFullYear() + 1); else d.setMonth(d.getMonth() + 1); endIso = d.toISOString(); }

    if (isNew) {
      await supabase.from('user_subscriptions').update({ status: 'cancelled', cancelled_at: now.toISOString() }).eq('user_id', userId).eq('status', 'active');
      const { error } = await supabase.from('user_subscriptions').insert({
        user_id: userId, plan_id: plan.id, billing_cycle: cycle, status: 'active',
        current_period_start: now.toISOString(), current_period_end: endIso,
      });
      if (error) throw error;
    } else {
      const { error } = await supabase.from('user_subscriptions')
        .update({ plan_id: plan.id, billing_cycle: cycle, current_period_end: endIso }).eq('id', sub.id);
      if (error) throw error;
    }
    await supabase.from('users').update({ plan: String(plan.name).toLowerCase() }).eq('id', userId);
    audit(isNew ? 'create' : 'update', 'user_subscriptions', sub.id || userId, isNew ? null : { plan_id: sub.plan_id, billing_cycle: sub.billing_cycle }, { plan_id: plan.id, billing_cycle: cycle });
    onSaved();
  }, isNew ? 'Subscription added' : 'Subscription updated');

  return (
    <Modal title={isNew ? 'Add subscription' : 'Change subscription'} onClose={onClose}
      sub={isNew ? 'Gives a user a plan without a payment request. Any current active plan is replaced.' : undefined}
      footer={<>
        <button className="ad-btn ad-btn-ghost" onClick={onClose} disabled={!!busy}>Cancel</button>
        <button className="ad-btn ad-btn-accent" onClick={save} disabled={!valid || !!busy}>{isNew ? 'Add subscription' : 'Save changes'}</button>
      </>}>
      {isNew ? (
        <div className="ad-field"><span>User</span>
          <input className="ad-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or email" />
          <select className="ad-select" size={5} value={userId} onChange={e => setUserId(e.target.value)} style={{ marginTop: 6, height: 'auto' }}>
            {list.map(u => <option key={u.id} value={u.id}>{userName(u)} · {u.email}</option>)}
          </select>
        </div>
      ) : (
        <div className="ad-field"><span>User</span><UserCell user={users[sub.user_id]} id={sub.user_id} /></div>
      )}
      <div className="ad-form">
        <label className="ad-field"><span>Plan</span>
          <select className="ad-select" value={planId} onChange={e => setPlanId(e.target.value)}>
            {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>
        <label className="ad-field"><span>Billing</span>
          <select className="ad-select" value={cycle} onChange={e => setCycle(e.target.value)}>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select></label>
        <label className="ad-field"><span>Renews / ends on</span>
          <input className="ad-input" type="date" value={end} onChange={e => setEnd(e.target.value)} />
          <small>Leave empty to start a fresh {cycle === 'annual' ? 'year' : 'month'} from today</small></label>
      </div>
    </Modal>
  );
}

async function reviewRequest(req, approve) {
  const { error: rpcErr } = await supabase.rpc('admin_review_subscription_request', { p_request_id: req.id, p_approve: approve });
  if (!rpcErr) { audit(approve ? 'approve' : 'reject', 'subscription_payment_requests', req.id, { status: 'pending' }, { status: approve ? 'approved' : 'rejected' }); return; }
  if (!isMissingFn(rpcErr)) throw rpcErr;

  // Fallback without the RPC
  if (approve) {
    const now = new Date();
    const end = new Date(now);
    if (req.billing_cycle === 'annual') end.setFullYear(end.getFullYear() + 1); else end.setMonth(end.getMonth() + 1);
    await supabase.from('user_subscriptions').update({ status: 'cancelled', cancelled_at: now.toISOString() })
      .eq('user_id', req.user_id).eq('status', 'active');
    const { error } = await supabase.from('user_subscriptions').insert({
      user_id: req.user_id, plan_id: req.plan_id, status: 'active', billing_cycle: req.billing_cycle || 'monthly',
      current_period_start: now.toISOString(), current_period_end: end.toISOString(),
    });
    if (error) throw error;
    if (req.plan_name) await supabase.from('users').update({ plan: req.plan_name }).eq('id', req.user_id);
  }
  const { error } = await supabase.from('subscription_payment_requests')
    .update({ status: approve ? 'approved' : 'rejected', reviewed_at: new Date().toISOString() }).eq('id', req.id);
  if (error) throw error;
  audit(approve ? 'approve' : 'reject', 'subscription_payment_requests', req.id, { status: 'pending' }, { status: approve ? 'approved' : 'rejected' });
}

export default function Subscriptions({ refreshCounts }) {
  const users = useUsers();
  const [tab, setTab] = useState('requests');
  const [editing, setEditing] = useState(null); // {} = new plan, or a plan row
  const [subEditing, setSubEditing] = useState(null); // {} = new subscription, or a subscription row
  const reqs = useRows('subscription_payment_requests');
  const subs = useRows('user_subscriptions', { select: '*, subscription_plans(name)', build: q => q.eq('status', 'active') });
  // subscription_plans has no created_at column, so useRows' default sort would fail (400)
  const [planRows, setPlanRows] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const loadPlans = async () => {
    const { data, error } = await supabase.from('subscription_plans').select('*').order('monthly_price', { ascending: true });
    setPlanRows(data || []); setPlansError(error ? error.message : null); setPlansLoading(false);
  };
  useEffect(() => { loadPlans(); }, []);
  const plans = { rows: planRows, loading: plansLoading, error: plansError, missing: false, reload: loadPlans };
  const [busy, run] = useAction();
  const pending = reqs.rows.filter(r => r.status === 'pending');
  const sortedPlans = [...plans.rows].sort((a, b) => Number(a.monthly_price) - Number(b.monthly_price));

  const act = (r, approve) => {
    if (!window.confirm(`${approve ? 'Approve' : 'Reject'} ${r.plan_name || 'plan'} payment of ${usd(r.amount)}?`)) return;
    run(r.id, async () => {
      await reviewRequest(r, approve);
      await notify(r.user_id, {
        title: approve ? `Your ${r.plan_name || ''} plan is active`.replace('  ', ' ') : 'Your plan payment was declined',
        description: approve ? 'Thanks for upgrading. Your new features are ready to use.' : 'We couldn’t confirm your payment. Contact support if you think this is a mistake.',
        category: 'payment', icon_class: approve ? 'ti-crown' : 'ti-circle-x', action_url: '/billing',
      });
      reqs.reload(); subs.reload(); refreshCounts?.();
    }, approve ? 'Plan activated' : 'Request rejected');
  };

  const cancelSub = (s) => {
    if (!window.confirm('Cancel this subscription now? The user drops back to the Free plan.')) return;
    run(s.id, async () => {
      const { error } = await supabase.from('user_subscriptions').update({ status: 'cancelled', cancelled_at: new Date().toISOString() }).eq('id', s.id);
      if (error) throw error;
      await supabase.from('users').update({ plan: 'free' }).eq('id', s.user_id);
      audit('cancel', 'user_subscriptions', s.id, { status: 'active' }, { status: 'cancelled' });
      subs.reload();
    }, 'Subscription cancelled');
  };

  return (
    <>
      <PageHead title="Subscriptions" sub="Approve plan payments, manage active subscriptions and edit the plans users can buy." />
      <Tabs value={tab} onChange={setTab} items={[
        { key:'requests', label:'Payment requests', count: pending.length },
        { key:'active',   label:'Active subscriptions', count: subs.rows.length },
        { key:'plans',    label:'Plans' },
      ]} />

      {tab === 'requests' && (
        <DataTable rows={reqs.rows} loading={reqs.loading} error={reqs.error} missing={reqs.missing} table="subscription_payment_requests" empty="No plan payments yet"
          columns={[
            { key:'created_at', label:'Date', render:r => fmtDateTime(r.created_at) },
            { key:'user', label:'User', render:r => <UserCell user={users[r.user_id]} id={r.user_id} /> },
            { key:'plan', label:'Plan', render:r => <div className="ad-user"><span>{r.plan_name}</span><small style={{ textTransform:'capitalize' }}>{r.billing_cycle}</small></div> },
            { key:'amount', label:'Amount', align:'right', render:r => usd(r.amount) },
            { key:'coin', label:'Paid with', render:r => r.coin ? `${r.coin}${r.network ? ` · ${r.network}` : ''}` : '—' },
            { key:'tx_hash', label:'Tx hash', render:r => <span className="ad-mono">{r.tx_hash ? `${r.tx_hash.slice(0, 14)}…` : '—'}</span> },
            { key:'proof', label:'Proof', render:r => <ProofButton path={r.proof_url} /> },
            { key:'act', label:'Status', align:'right', render:r => r.status !== 'pending' ? <Pill status={r.status} /> : (
              <div className="ad-row-actions">
                <button className="ad-btn ad-btn-ok ad-btn-sm" disabled={busy === r.id} onClick={() => act(r, true)}><Check />Approve</button>
                <button className="ad-btn ad-btn-no ad-btn-sm" disabled={busy === r.id} onClick={() => act(r, false)}><X />Reject</button>
              </div>
            ) },
          ]} />
      )}

      {tab === 'active' && (
        <DataTable rows={subs.rows} loading={subs.loading} error={subs.error} missing={subs.missing} table="user_subscriptions" empty="No active subscriptions"
          toolbar={<button className="ad-btn ad-btn-accent" onClick={() => setSubEditing({})}><Plus />Add subscription</button>}
          columns={[
            { key:'user', label:'User', render:r => <UserCell user={users[r.user_id]} id={r.user_id} /> },
            { key:'plan', label:'Plan', render:r => r.subscription_plans?.name || '—' },
            { key:'billing_cycle', label:'Billing', render:r => <span style={{ textTransform:'capitalize' }}>{r.billing_cycle || '—'}</span> },
            { key:'current_period_start', label:'Started', render:r => fmtDate(r.current_period_start) },
            { key:'current_period_end', label:'Renews / ends', render:r => fmtDate(r.current_period_end) },
            { key:'act', label:'', align:'right', render:r => (
              <div className="ad-row-actions">
                <button className="ad-btn ad-btn-ghost ad-btn-sm" disabled={busy === r.id} onClick={() => setSubEditing(r)}><Pencil />Change</button>
                <button className="ad-btn ad-btn-no ad-btn-sm" disabled={busy === r.id} onClick={() => cancelSub(r)}><Ban />Cancel</button>
              </div>
            ) },
          ]} />
      )}

      {tab === 'plans' && (
        <DataTable rows={sortedPlans} loading={plans.loading} error={plans.error} missing={plans.missing} table="subscription_plans"
          empty="No plans yet. Add your first plan." onRowClick={setEditing}
          toolbar={<button className="ad-btn ad-btn-accent" onClick={() => setEditing({})}><Plus />Add plan</button>}
          columns={[
            { key:'name', label:'Plan', render:r => <strong>{r.name}</strong> },
            { key:'monthly_price', label:'Monthly', align:'right', render:r => usd(r.monthly_price) },
            { key:'annual_price', label:'Annual (per month)', align:'right', render:r => usd(r.annual_price) },
            { key:'max_copy_traders', label:'Copy traders', align:'right' },
            { key:'max_allocation', label:'Max allocation', align:'right', render:r => r.max_allocation != null ? usd(r.max_allocation, 0) : '—' },
            { key:'features', label:'Features', align:'right', render:r => `${Object.values(r.features || {}).filter(Boolean).length} active` },
          ]} />
      )}

      {subEditing && <SubModal sub={subEditing} plans={sortedPlans} users={users} onClose={() => setSubEditing(null)}
        onSaved={() => { setSubEditing(null); subs.reload(); refreshCounts?.(); }} />}
      {editing && <PlanModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); plans.reload(); }} />}
    </>
  );
}